import {
  Component,
  OnInit,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core'
import { FormsModule } from '@angular/forms'
import { DialogRef, DIALOG_DATA } from '@angular/cdk/dialog'

import { MatButtonModule } from '@angular/material/button'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatIconModule } from '@angular/material/icon'
import { MatTableDataSource, MatTableModule } from '@angular/material/table'
import { MatCheckboxModule } from '@angular/material/checkbox'
import { MatChipsModule } from '@angular/material/chips'
import { MatTooltipModule } from '@angular/material/tooltip'

import { firstValueFrom } from 'rxjs'
import { CompanyService } from '../../../services/company.service'
import { ProductService } from '../../../services/product.service'
import { TranslationService } from '../../../services/translation.service'
import { ConnectedCompaniesResponse } from '../../../types/companyConnection'
import { ShareRow } from '../../../types/products'
import { ApiError } from '../../../types/apiError'

type ShareProductsDialogData = {
  productId: string
  productName?: string
}

@Component({
  selector: 'app-share-products-dialog',
  standalone: true,
  imports: [
    FormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatTableModule,
    MatCheckboxModule,
    MatChipsModule,
    MatTooltipModule,
  ],
  templateUrl: './share-products-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./share-products-dialog.component.scss'],
})
export class ShareProductsDialogComponent implements OnInit {
  data = inject<ShareProductsDialogData>(DIALOG_DATA)
  private dialogRef = inject<DialogRef<ShareProductsDialogComponent>>(DialogRef)
  private companyService = inject(CompanyService)
  private productService = inject(ProductService)
  translationService = inject(TranslationService)

  displayedColumns: string[] = ['name', 'overrides', 'remove']
  sharesDataSource = new MatTableDataSource<ShareRow>([])

  // Connected companies (supply chain)
  connectedCompanies: ConnectedCompaniesResponse = []

  selectedToCompanyId: string | null = null

  // Overrides
  overrideProductSupplier = false
  overrideComponentSuppliers = false

  errorMessage: string | null = null

  async ngOnInit(): Promise<void> {
    this.errorMessage = null
    await this.loadConnectedCompanies()
    await this.loadShares()
  }

  /**
   * Loads current shares for this product.
   * @returns {Promise<void>} Resolves when the table has been refreshed.
   */
  async loadShares(): Promise<void> {
    this.errorMessage = null

    try {
      const res = await firstValueFrom(
        this.productService.getProductShares(this.data.productId),
      )

      const rows: ShareRow[] = (res ?? []).map((s) => ({
        toCompanyId: s.toCompanyId,
        toCompanyName: s.toCompanyName,
        overrideProductSupplier: s.overrideProductSupplier,
        overrideComponentSuppliers: s.overrideComponentSuppliers,
      }))

      this.sharesDataSource.data = rows

      // If the selected company is no longer available (because shares changed), clear it.
      if (this.selectedToCompanyId) {
        const availableIds = new Set(
          this.getAvailableConnections().map((c) => c.id),
        )
        if (!availableIds.has(this.selectedToCompanyId)) {
          this.selectedToCompanyId = null
        }
      }
    } catch (err) {
      const apiErr = err as ApiError
      this.errorMessage = apiErr.message
      this.sharesDataSource.data = []
    }
  }

  /**
   * Loads the connected companies for the sharing company (supply chain list).
   * @returns {Promise<void>} Resolves when connected companies have been loaded.
   */
  private async loadConnectedCompanies(): Promise<void> {
    this.errorMessage = null

    try {
      const res = await firstValueFrom(
        this.companyService.getMyConnectedCompanies(),
      )

      this.connectedCompanies = res ?? []
    } catch {
      this.errorMessage = this.translationService.getFailedToLoadCompaniesText()
      this.connectedCompanies = []
    }
  }

  /**
   * Returns connected companies that the product is not yet shared with.
   * @returns {ConnectedCompaniesResponse} Available recipient companies.
   */
  getAvailableConnections(): ConnectedCompaniesResponse {
    const sharedIds = new Set(
      this.sharesDataSource.data.map((s) => s.toCompanyId),
    )
    return this.connectedCompanies.filter((c) => !sharedIds.has(c.id))
  }

  /**
   * Creates share records for the selected recipient companies.
   * @param {string[]} toCompanyIds Recipient company IDs.
   * @returns {Promise<void>} Resolves after shares have been created and the table refreshed.
   */
  private async shareToCompanies(toCompanyIds: string[]): Promise<void> {
    this.errorMessage = null

    try {
      await firstValueFrom(
        this.productService.shareProduct({
          productId: this.data.productId,
          toCompanyIds,
          overrideProductSupplier: this.overrideProductSupplier,
          overrideComponentSuppliers: this.overrideComponentSuppliers,
        }),
      )

      await this.loadShares()
    } catch (err) {
      const apiErr = err as ApiError
      this.errorMessage = apiErr.message
    }
  }

  /**
   * Shares the product to the currently selected company.
   * @returns {Promise<void>} Resolves after the share is created and the table refreshed.
   */
  async addShare(): Promise<void> {
    if (!this.selectedToCompanyId) {
      return
    }

    const toCompanyId = this.selectedToCompanyId

    await this.shareToCompanies([toCompanyId])

    // Clear selection after refresh so listbox state stays consistent.
    this.selectedToCompanyId = null
  }

  /**
   * Revokes sharing for a recipient company.
   * @param {string} companyId Recipient company ID.
   * @returns {Promise<void>} Resolves after the share has been removed and the table refreshed.
   */
  async revokeShare(companyId: string): Promise<void> {
    this.errorMessage = null

    try {
      await firstValueFrom(
        this.productService.revokeProductShare(this.data.productId, {
          companyId,
        }),
      )

      await this.loadShares()
      this.selectedToCompanyId = null
    } catch (err) {
      const apiErr = err as ApiError
      this.errorMessage = apiErr.message
    }
  }

  /**
   * Close the dialog.
   * @returns {void}
   */
  close(): void {
    this.dialogRef.close()
  }
}
